"use client";

import { motion, MotionValue, useScroll, useInView } from "framer-motion";
import { useRef } from "react";

interface ScrollAnimatedSnakePathProps {
  /** SVG path definition string */
  pathD: string;
  /** Scroll progress value (0-1) for desktop scroll-linked animation */
  scrollYProgress?: MotionValue<number>;
  /** Whether to use view-based animation (for mobile) instead of scroll-linked */
  useViewAnimation?: boolean;
  /** Animation duration for view-based animation */
  animationDuration?: number;
  /** SVG stroke width */
  strokeWidth?: number;
  /** SVG stroke color */
  strokeColor?: string;
  /** Additional CSS classes */
  className?: string;
  /** SVG viewBox */
  viewBox?: string;
}

/**
 * A reusable animated snake path component that can animate based on:
 * 1. Scroll progress (desktop) - linked to parent scroll position
 * 2. View-based animation (mobile) - animates when element enters viewport
 */
export default function ScrollAnimatedSnakePath({
  pathD,
  scrollYProgress,
  useViewAnimation = false,
  animationDuration = 1.5,
  strokeWidth = 0.05,
  strokeColor = "rgba(255,255,255,1)",
  className = "",
  viewBox = "0 0 100 100",
}: ScrollAnimatedSnakePathProps) {
  const ref = useRef<SVGSVGElement>(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  return (
    <svg
      ref={ref}
      className={`pointer-events-none ${className}`}
      viewBox={viewBox}
      preserveAspectRatio="none"
    >
      {useViewAnimation ? (
        <motion.path
          d={pathD}
          fill="none"
          strokeWidth={strokeWidth}
          stroke={strokeColor}
          initial={{ pathLength: 0 }}
          animate={isInView ? { pathLength: 1 } : { pathLength: 0 }}
          transition={{ duration: animationDuration, ease: "easeInOut" }}
        />
      ) : (
        <motion.path
          d={pathD}
          fill="none"
          strokeWidth={strokeWidth}
          stroke={strokeColor}
          style={{ pathLength: scrollYProgress }}
        />
      )}
    </svg>
  );
}
